import { useCallback, useEffect, useRef } from 'react';

import { sessionId, useApplicationState } from '@atlas/core';

import { POSTHOG_EVENTS } from 'common/constants';

import usePostHog from './usePostHog';

const useDemoGamePostHog = ({ gameId, isDemo, source }) => {
  const postHog = usePostHog();
  const isInitializedRef = useRef(false);
  const isSentRef = useRef(false);
  const sourceRef = useRef(source);
  const prevIsDemoRef = useRef(isDemo);

  const {
    account,
    authenticated,
    wallet: { detailedBalance: { BONUS = 0, CASH = 0, FROZEN = 0 } = {} },
  } = useApplicationState();

  const getBaseData = useCallback(() => {
    return {
      client_id: account.id ?? null,
      game_id: gameId,
      session_id: sessionId,
      source: sourceRef.current ?? null,
      user_bonus_balance: authenticated ? BONUS : null,
      user_cash_balance: authenticated ? CASH : null,
      user_frozen_balance: authenticated ? FROZEN : null,
    };
  }, [account.id, authenticated, gameId, BONUS, CASH, FROZEN]);

  useEffect(() => {
    if (!isDemo || isInitializedRef.current) return;
    isInitializedRef.current = true;

    postHog.concat(POSTHOG_EVENTS.DEMO_GAME_PLAY, {
      ...getBaseData(),
      real_play_click_timestamp: null,
      target_URL: null,
      timestamp: Date.now(),
    });
  }, [getBaseData, isDemo, postHog]);

  useEffect(() => {
    const wasDemo = prevIsDemoRef.current;
    prevIsDemoRef.current = isDemo;

    const isDemoExit = wasDemo && !isDemo && isInitializedRef.current && !isSentRef.current;

    if (isDemoExit) {
      isSentRef.current = true;
      postHog.send(POSTHOG_EVENTS.DEMO_GAME_PLAY, {
        target_URL: window.location.pathname + window.location.search,
      });
    }
  }, [isDemo]);

  useEffect(() => {
    return () => {
      if (!isInitializedRef.current || isSentRef.current) return;
      postHog.send(POSTHOG_EVENTS.DEMO_GAME_PLAY, {
        target_URL: window.location.pathname + window.location.search,
      });
    };
  }, []);

  const handleRealPlayClick = useCallback(
    ({ target_URL }) => {
      isSentRef.current = true;
      postHog.send(POSTHOG_EVENTS.DEMO_GAME_PLAY, {
        real_play_click_timestamp: Date.now(),
        target_URL,
      });
    },
    [postHog],
  );

  return {
    handleRealPlayClick,
  };
};

export default useDemoGamePostHog;
